import type { ComparisonPage } from '../types';

export const sigmaVsLooker: ComparisonPage = {
  slug: 'sigma-vs-looker',
  group: 'bi',
  ours: 'sigma',
  theirs: 'looker',
  title: 'Sigma vs Looker: governed models or spreadsheet freedom?',
  metaTitle: 'Sigma vs Looker (2026): Features, Pricing, Who Should Pick Which',
  description:
    'Looker centers on a LookML model the data team owns. Sigma centers on a spreadsheet business users own. Here is how that plays out on Snowflake and BigQuery.',
  keywords: ['sigma vs looker', 'looker alternative', 'sigma computing vs looker', 'looker to sigma', 'lookml alternative'],
  published: '2026-11-03',
  updated: '2026-11-03',
  tldr:
    'Pick Looker if you want every metric defined once in LookML, reviewed in git, and served the same way everywhere, and especially if you are on Google Cloud. Pick Sigma if the people asking questions want to explore and build in a spreadsheet without waiting on a model change, or need to enter data back into the warehouse.',
  differences: [
    { heading: 'Model first vs workbook first', body: 'In Looker, analysts define LookML views and explores before anyone builds a chart. In Sigma, users start in a workbook on warehouse tables.' },
    { heading: 'Write-back', body: 'Looker has no native write-back; Google’s own guide builds it with custom code. Sigma input tables are built in.' },
    { heading: 'Google Cloud fit', body: 'Looker is part of Google Cloud, with Gemini features and BigQuery ties. Sigma runs on any of the major clouds.' },
  ],
  sections: [
    {
      heading: 'Governance',
      paragraphs: [
        'LookML is Looker’s biggest strength. Metric logic lives in code, versioned in git, and every dashboard and AI answer uses the same definitions. Teams that have been burned by conflicting numbers value that discipline.',
        'The cost is speed. A new field or join often means a model change by the data team. Sigma has data models too, but users can also build directly in a workbook, which is faster and less controlled.',
      ],
    },
    {
      heading: 'AI and recent changes',
      paragraphs: [
        'Looker’s conversational analytics became generally available in November 2025, and Google announced more agent features at Next ’26, some still in preview. Looker reports were deprecated in July 2026. Sigma has also shipped assistants, agents, and an MCP server through 2026.',
      ],
    },
    {
      heading: 'Pricing',
      paragraphs: [
        'Neither publishes license prices. Looker sells editions with developer, standard, and viewer licenses on one- to three-year terms. Sigma sells annual contracts. Both run queries on your warehouse, so add that compute to either quote.',
      ],
    },
  ],
  pickThem: [
    'You want all metric logic in code, reviewed in git, served consistently everywhere.',
    'You are on Google Cloud and BigQuery and want Gemini built in.',
    'You have analytics engineers to maintain LookML.',
  ],
  pickUs: [
    'Business users want to explore and build without waiting on model changes.',
    'Users need to enter forecasts, approvals, or adjustments into the warehouse.',
    'You want Python and SQL next to a spreadsheet in one workbook.',
  ],
  byTeam: [
    { team: 'Analytics engineering team on BigQuery', pick: 'theirs', why: 'LookML in git and deep Google Cloud integration.' },
    { team: 'Finance or FP&A team', pick: 'ours', why: 'Spreadsheet work and input tables without model changes.' },
    { team: 'Team frustrated by slow model changes', pick: 'ours', why: 'Users can build in a workbook and promote what works.' },
  ],
  faq: [
    {
      question: 'Does Sigma replace Looker?',
      answer: 'Yes, if your users want more freedom than a model-first tool gives them and you can govern metrics in warehouse tables or Sigma data models. No, if centralized LookML governance is the reason you chose Looker. Teams that like LookML but want a newer tool also look at [Omni](/compare/sigma-vs-omni).',
    },
    {
      question: 'Is Looker the same as Looker Studio?',
      answer: 'No. Looker is Google’s enterprise BI platform built on LookML. Looker Studio is Google’s free dashboard tool and a separate product.',
    },
  ],
  related: ['sigma-vs-omni', 'sigma-vs-tableau', 'sigma-vs-power-bi'],
};
